import { useEffect, useState } from "react";
import { apiJson } from "../utils/api";

/**
 * V4.0 关系反馈表单：提交后软写回关系地图。
 */
export default function RelationshipFeedbackForm({
  userId,
  source = "manual",
  targetUserId = null,
  simulationId = null,
  defaultTopic = "general",
  compact = false,
  onSubmitted,
}) {
  const [meta, setMeta] = useState(null);
  const [kind, setKind] = useState("need_discuss");
  const [topic, setTopic] = useState(defaultTopic || "general");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [doneMsg, setDoneMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { res, data } = await apiJson("/api/ai/relationship-feedback/meta");
        if (!cancelled && res.ok) setMeta(data);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (defaultTopic) setTopic(defaultTopic);
  }, [defaultTopic]);

  const submit = async () => {
    if (!userId || busy) return;
    setBusy(true);
    setDoneMsg("");
    try {
      const { res, data } = await apiJson("/api/ai/relationship-feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: userId,
          kind,
          topic_key: topic,
          content: content.trim(),
          source,
          target_user_id: targetUserId ? Number(targetUserId) : null,
          simulation_id: simulationId ? Number(simulationId) : null,
        }),
      });
      if (!res.ok) throw new Error(data?.detail || "提交失败");
      setDoneMsg(data.applied_summary || "已记录，并更新了关系观察");
      setContent("");
      if (onSubmitted) onSubmitted(data);
    } catch (err) {
      alert(err.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  const kinds = meta?.kinds || [
    { key: "resonate", label: "更容易共鸣" },
    { key: "friction", label: "需要磨合" },
    { key: "surprise", label: "有意外发现" },
    { key: "need_discuss", label: "值得提前聊" },
    { key: "support", label: "感到被支持" },
  ];
  const topics = meta?.topics || [{ key: "general", label: "整体感受" }];

  return (
    <div
      className={`rounded-2xl border border-violet-100 bg-white space-y-3 ${
        compact ? "p-4" : "p-5"
      }`}
    >
      <div>
        <h3 className="font-semibold text-gray-900 text-sm">
          告诉 AI：这次关系里你观察到了什么？
        </h3>
        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
          反馈会软更新关系地图，帮助智能体更了解你的模式——不是匹配分，也不是预测。
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {kinds.map((k) => (
          <button
            key={k.key}
            type="button"
            onClick={() => setKind(k.key)}
            className={`px-3 py-1.5 rounded-full text-xs border ${
              kind === k.key
                ? "bg-violet-600 text-white border-violet-600"
                : "bg-white text-gray-600 border-gray-200"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {topics.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTopic(t.key)}
            className={`px-2.5 py-1 rounded-full text-[11px] border ${
              topic === t.key
                ? "bg-rose-50 text-rose-800 border-rose-200"
                : "bg-gray-50 text-gray-600 border-gray-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value.slice(0, 500))}
        rows={compact ? 2 : 3}
        placeholder="可选：用一两句话补充具体感受…"
        className="w-full border rounded-xl px-3 py-2 text-sm resize-none"
      />
      <button
        type="button"
        disabled={busy || !userId}
        onClick={submit}
        className="px-5 py-2.5 rounded-full bg-violet-600 text-white text-sm font-semibold disabled:opacity-50"
      >
        {busy ? "写入中…" : "提交反馈并更新地图"}
      </button>
      {doneMsg && (
        <p className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-xl px-3 py-2">
          {doneMsg}
        </p>
      )}
      <p className="text-[11px] text-gray-400 leading-relaxed">
        {meta?.disclaimer ||
          "反馈仅用于认识关系，不构成婚姻预测。"}
      </p>
    </div>
  );
}
